import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Search, Trash2, MessageSquare, UserPlus, UserMinus, CheckCircle2, User } from 'lucide-react';

export const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dirSearch, setDirSearch] = useState('');
  const [dirLoading, setDirLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [c, f, fg] = await Promise.all([
        userAPI.getContacts(),
        userAPI.getFollowers(),
        userAPI.getFollowing(),
      ]);
      setContacts(c.data);
      setFollowers(f.data);
      setFollowing(fg.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const searchDirectory = async () => {
    setDirLoading(true);
    try {
      const res = await userAPI.getDirectory(dirSearch, 0, 50);
      setDirectory(res.data);
    } catch (e) { toast.error('Search failed'); }
    finally { setDirLoading(false); }
  };

  // Auto-search when the "Find users" tab opens
  useEffect(() => { searchDirectory(); /* eslint-disable-next-line */ }, []);

  const doFollow = async (uid) => {
    try { await userAPI.follow(uid); toast.success('Following'); load(); searchDirectory(); }
    catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };
  const doUnfollow = async (uid) => {
    try { await userAPI.unfollow(uid); toast.success('Unfollowed'); load(); searchDirectory(); }
    catch (e) { toast.error('Failed'); }
  };
  const addContact = async (uid) => {
    try { await userAPI.addContact(uid); toast.success('Added to contacts'); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };
  const removeContact = async (id) => {
    if (!window.confirm('Remove this contact?')) return;
    try { await userAPI.removeContact(id); toast.success('Removed'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const openMessage = (uid) => navigate(`/user/messages?with=${uid}`);
  const openProfile = (u) => {
    const handle = u.username || u.handle;
    if (handle) navigate(`/@${handle}`); else toast.info('This user has no public handle');
  };

  const filteredContacts = contacts.filter(c =>
    !search || c.contact_email?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_name?.toLowerCase().includes(search.toLowerCase())
  );

  const isFollowing = (uid) => following.some(f => f.user_id === uid || f.id === uid);

  return (
    <FocusedLayout title="Contacts & Directory">
      <div className="space-y-6" data-testid="contacts-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Contacts &amp; Social</h1>
          <p className="mt-2 text-muted-foreground">Find people on the platform, follow them, save contacts and start conversations.</p>
        </div>

        <Tabs defaultValue="find">
          <TabsList>
            <TabsTrigger value="find" data-testid="tab-find">Find users</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="following">Following ({following.length})</TabsTrigger>
            <TabsTrigger value="followers">Followers ({followers.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="find">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search by name, username or email..." value={dirSearch}
                    onChange={(e) => setDirSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchDirectory()}
                    data-testid="search-users-input" />
                  <Button onClick={searchDirectory} disabled={dirLoading} data-testid="search-users-btn">Search</Button>
                </div>
              </CardHeader>
              <CardContent>
                {dirLoading ? <p className="text-center py-6 text-muted-foreground">Searching...</p> :
                 directory.length === 0 ? <p className="text-center py-8 text-muted-foreground">No users found.</p> : (
                  <div className="divide-y divide-border">
                    {directory.map(u => (
                      <div key={u.id || u.user_id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`user-row-${u.id || u.user_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold shrink-0">
                            {(u.name || u.username || u.email || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate flex items-center gap-1">
                              @{u.username || u.email?.split('@')[0] || 'user'}
                              {u.verified && <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {u.name || 'User'}
                              {u.followers_count != null && ` · ${u.followers_count} follower${u.followers_count !== 1 ? 's' : ''}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => openProfile(u)} data-testid={`view-profile-${u.id || u.user_id}`}>
                            <User className="h-3 w-3 mr-1" /> Profile
                          </Button>
                          {isFollowing(u.id || u.user_id) ? (
                            <Button size="sm" variant="outline" onClick={() => doUnfollow(u.id || u.user_id)} data-testid={`unfollow-${u.id || u.user_id}`}>
                              <UserMinus className="h-3 w-3 mr-1" /> Unfollow
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => doFollow(u.id || u.user_id)} data-testid={`follow-${u.id || u.user_id}`}>
                              <UserPlus className="h-3 w-3 mr-1" /> Follow
                            </Button>
                          )}
                          <Button size="sm" onClick={() => openMessage(u.id || u.user_id)} data-testid={`msg-${u.id || u.user_id}`}>
                            <MessageSquare className="h-3 w-3 mr-1" /> Message
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Filter contacts..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </CardHeader>
              <CardContent>
                {loading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> :
                 filteredContacts.length === 0 ? <p className="text-center py-8 text-muted-foreground">No saved contacts yet.</p> : (
                  <div className="divide-y divide-border">
                    {filteredContacts.map(c => (
                      <div key={c.id} className="py-3 flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{c.contact_name || c.contact_email}</p>
                          <p className="text-xs text-muted-foreground">{c.contact_email}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => openMessage(c.contact_id)}><MessageSquare className="h-3 w-3 mr-1" /> Message</Button>
                          <Button size="sm" variant="outline" onClick={() => removeContact(c.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="following">
            <Card>
              <CardContent className="pt-6">
                {following.length === 0 ? <p className="text-center py-8 text-muted-foreground">You&apos;re not following anyone yet.</p> : (
                  <div className="divide-y divide-border">
                    {following.map(u => (
                      <div key={u.user_id || u.id} className="py-3 flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{u.name || u.username || u.email}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => openMessage(u.user_id || u.id)}><MessageSquare className="h-3 w-3 mr-1" /> Message</Button>
                          <Button size="sm" variant="outline" onClick={() => doUnfollow(u.user_id || u.id)}><UserMinus className="h-3 w-3 mr-1" /> Unfollow</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="followers">
            <Card>
              <CardContent className="pt-6">
                {followers.length === 0 ? <p className="text-center py-8 text-muted-foreground">No followers yet.</p> : (
                  <div className="divide-y divide-border">
                    {followers.map((u) => {
                      const uid = u.user_id || u.id;
                      const mutual = isFollowing(uid);
                      return (
                        <div key={uid} className="py-3 flex items-center justify-between gap-3" data-testid={`follower-row-${uid}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-medium shrink-0">
                              {(u.name || u.username || u.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium truncate">{u.name || u.username || u.email}</p>
                                {mutual && (
                                  <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[10px]" data-testid={`mutual-badge-${uid}`}>
                                    <CheckCircle2 className="h-3 w-3 mr-0.5" /> Followed you back
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {u.username && (
                              <Button size="sm" variant="outline" onClick={() => openProfile(u)} data-testid={`view-profile-${uid}`}>
                                <User className="h-3 w-3 mr-1" /> Profile
                              </Button>
                            )}
                            <Button size="sm" onClick={() => openMessage(uid)} data-testid={`message-${uid}`}>
                              <MessageSquare className="h-3 w-3 mr-1" /> Message
                            </Button>
                            {mutual ? null : (
                              <Button size="sm" variant="outline" onClick={() => doFollow(uid)} data-testid={`follow-back-${uid}`}>
                                <UserPlus className="h-3 w-3 mr-1" /> Follow back
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </FocusedLayout>
  );
};
